import i18n from '@/utils/i18n';
import moment from 'moment';
import React from 'react';
import { Checkbox } from '@/components/checkbox';
import { createStyles } from '@/contexts/theme-context';
import { formatCurrency } from '@/utils/money';
import { formatSummaryDateWithYear } from '@/utils/date';
import { PaymentItemType } from '@/enums';
import { PaymentsListItem } from './payments-list-item';
import { StyleProp, ViewStyle } from 'react-native';
import { Typography, TypographyVariant } from '@usepepper/ui/atoms/typography';
import { UnpaidItemType } from '@/contexts/payments-context';

interface Props {
  bottomLeftTypography?: TypographyVariant;
  bottomRightTypography?: TypographyVariant;
  containerStyle?: StyleProp<ViewStyle>;
  currencyCode: string | null;
  isChecked?: boolean;
  isSelected?: boolean;
  onCheck?: () => void;
  onPress: () => void;
  showCheckbox?: boolean;
  topLeftTypography?: TypographyVariant;
  topRightTypography?: TypographyVariant;
  unpaidItem: UnpaidItemType;
}

export const UnpaidInvoiceListItem: React.FC<Props> = ({
  bottomLeftTypography = 'paragraph-md',
  bottomRightTypography = 'paragraph-md',
  containerStyle,
  currencyCode,
  isChecked = false,
  isSelected,
  onCheck,
  onPress,
  showCheckbox,
  topLeftTypography = 'paragraph-base-600',
  topRightTypography = 'paragraph-base',
  unpaidItem,
}) => {
  const [styles, theme] = useStyles();

  const isPastDue =
    unpaidItem.type === PaymentItemType.Invoice &&
    unpaidItem.dueAt !== null &&
    unpaidItem.dueAt < moment().startOf('day').toDate();

  const daysPastDue =
    isPastDue && unpaidItem.dueAt
      ? moment()
          .startOf('day')
          .diff(moment(unpaidItem.dueAt).startOf('day'), 'days')
      : 0;

  return (
    <PaymentsListItem
      bottomLeft={
        isPastDue && daysPastDue > 0 ? (
          <Typography
            color={theme.sk.color.text.tertiary}
            variant={bottomLeftTypography}
          >
            {i18n.t(daysPastDue === 1 ? 'day_past_due' : 'days_past_due', {
              count: daysPastDue,
            })}
          </Typography>
        ) : unpaidItem.dateText ? (
          <Typography
            color={theme.sk.color.text.tertiary}
            variant={bottomLeftTypography}
          >
            {unpaidItem.dateText}
          </Typography>
        ) : undefined
      }
      bottomRight={
        unpaidItem.type === PaymentItemType.Invoice && unpaidItem.dueAt ? (
          <Typography
            color={theme.sk.color.text.tertiary}
            variant={bottomRightTypography}
          >
            {i18n.t('invoice_number', {
              number: unpaidItem.displayNumber,
            })}
          </Typography>
        ) : undefined
      }
      containerStyle={containerStyle}
      isSelected={isSelected}
      leftComponent={
        showCheckbox && onCheck ? (
          <Checkbox
            accessibilityLabel={unpaidItem.displayNumber}
            isSelected={isChecked}
            onSelect={onCheck}
            style={styles.checkboxStyle}
            trackingData={{
              name: 'unpaid-invoice',
              properties: {
                invoiceUUID: unpaidItem.uuid,
                selecting: !isChecked,
              },
            }}
          />
        ) : undefined
      }
      onPress={onPress}
      testID="unpaid-invoice-list-item"
      topLeft={
        <Typography
          color={
            isPastDue ? theme.sk.color.text.error : theme.sk.color.text.primary
          }
          variant={topLeftTypography}
        >
          {unpaidItem.type === PaymentItemType.Invoice
            ? unpaidItem.dueAt
              ? i18n.t('payment_pay_by_date', {
                  date: formatSummaryDateWithYear(unpaidItem.dueAt),
                })
              : unpaidItem.displayNumber
            : unpaidItem.displayNumber}
        </Typography>
      }
      topRight={
        <Typography
          color={theme.sk.color.text.primary}
          variant={topRightTypography}
        >
          {formatCurrency(unpaidItem.amountInMicros, currencyCode)}
        </Typography>
      }
    />
  );
};

const useStyles = createStyles(() => ({
  checkboxStyle: {
    paddingHorizontal: 0,
    paddingVertical: 0,
  },
}));
